/**
 * AI Sourcing Panel - Used from Job Details page
 * Launches AI sourcing for a specific job
 */
import React, { useState, useEffect } from 'react';
import { X, Sparkles, Search, Filter, Users, TrendingUp } from 'lucide-react';
import toast from 'react-hot-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AISourcingPanel = ({ job, onClose, onCandidatesFound }) => {
  const [loading, setLoading] = useState(false);
  const [idealProfile, setIdealProfile] = useState(null);
  const [availablePortals, setAvailablePortals] = useState([]);
  const [loadingPortals, setLoadingPortals] = useState(true);
  const [preferences, setPreferences] = useState({
    must_have_skills: job.required_skills || [],
    good_to_have_skills: [],
    locations: [job.location].filter(Boolean),
    location_type: 'hybrid',
    experience_min: null,
    experience_max: null,
    selected_portal_ids: [], // Dynamic portal selection
    max_candidates_per_portal: 20
  });

  // Load available sourcing portals on component mount
  useEffect(() => {
    loadAvailablePortals();
  }, []);

  const loadAvailablePortals = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/sourcing-portals/?active_only=true`);
      if (response.ok) {
        const portals = await response.json();
        setAvailablePortals(portals);
        
        // Auto-select default portals
        const defaultPortalIds = portals
          .filter(portal => portal.is_default)
          .map(portal => portal.id);
        
        if (defaultPortalIds.length > 0) {
          setPreferences(prev => ({
            ...prev,
            selected_portal_ids: defaultPortalIds
          }));
        }
      } else {
        console.warn('Failed to load sourcing portals, using fallback');
        // Fallback to seed default portals
        await seedDefaultPortals();
      }
    } catch (error) {
      console.error('Error loading portals:', error);
      // Use fallback portals
      setAvailablePortals([
        {
          id: 'internal_db',
          name: 'Internal Database',
          portal_type: 'company_database',
          is_active: true,
          is_default: true
        }
      ]);
      setPreferences(prev => ({
        ...prev,
        selected_portal_ids: ['internal_db']
      }));
    } finally {
      setLoadingPortals(false);
    }
  };

  const seedDefaultPortals = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/sourcing-portals/seed-defaults`, {
        method: 'POST'
      });
      if (response.ok) {
        // Reload portals after seeding
        await loadAvailablePortals();
      }
    } catch (error) {
      console.error('Error seeding default portals:', error);
    }
  };

  const startSourcing = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/ai-sourcing/jobs/${job.id}/run`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          job_id: job.id,
          preferences
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to start AI sourcing');
      }

      setIdealProfile(data.ideal_profile);
      
      toast.success(`AI Sourcing completed! Found ${data.total_candidates_found || 0} matching candidates. Redirecting to view results...`);
      
      // Store job and run ID for navigation
      const jobId = job.id;
      const runId = data.sourcing_run_id;
      
      // Call callback with results
      if (onCandidatesFound) {
        setTimeout(() => {
          onCandidatesFound(runId, jobId, data);
        }, 1500); // Small delay to let the data save and show toast
      } else {
        // If no callback, just close the modal
        setTimeout(() => {
          onClose();
        }, 1500);
      }
    } catch (error) {
      console.error('AI Sourcing error:', error);
      
      // Provide more specific error messages
      if (error.message.includes('404')) {
        toast.error('Job not found. Please refresh and try again.');
      } else if (error.message.includes('400')) {
        toast.error('Job is not eligible for AI sourcing (may be closed or filled).');
      } else if (error.message.includes('500')) {
        toast.error('AI service is currently unavailable. Please try again later.');
      } else {
        toast.error(`AI Sourcing failed: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-purple-600 to-blue-600 text-white p-6">
          <div className="flex justify-between items-start">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-6 h-6" />
                <h2 className="text-2xl font-bold">AI Candidate Sourcing</h2>
              </div>
              <p className="text-purple-100">Smart candidate matching for {job.title}</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg transition"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="p-6">
          {/* Job Context */}
          <div className="bg-blue-50 rounded-lg p-4 mb-6">
            <h3 className="font-semibold mb-2 flex items-center gap-2">
              <Users className="w-4 h-4" />
              Job Requirements
            </h3>
            <div className="text-sm space-y-1">
              <p><span className="font-medium">Title:</span> {job.title}</p>
              <p><span className="font-medium">Location:</span> {job.location || 'Not specified'}</p>
              {job.required_skills && job.required_skills.length > 0 && (
                <p><span className="font-medium">Skills:</span> {job.required_skills.join(', ')}</p>
              )}
            </div>
          </div>

          {/* Ideal Profile (if generated) */}
          {idealProfile && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <h3 className="font-semibold text-green-900 mb-2 flex items-center gap-2">
                <TrendingUp className="w-4 h-4" />
                AI-Generated Ideal Candidate Profile
              </h3>
              <p className="text-sm text-green-800 mb-3">{idealProfile.summary}</p>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="font-medium text-green-900">Key Skills:</span>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {idealProfile.key_skills.map((skill, idx) => (
                      <span key={idx} className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs">
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
                <div>
                  <span className="font-medium text-green-900">Experience:</span>
                  <p className="text-green-700">{idealProfile.experience_range}</p>
                </div>
              </div>
            </div>
          )}

          {/* Sourcing Preferences */}
          <div className="space-y-4 mb-6">
            <h3 className="font-semibold flex items-center gap-2">
              <Filter className="w-4 h-4" />
              Sourcing Preferences
            </h3>

            {/* Must-have Skills */}
            <div>
              <label className="block text-sm font-medium mb-2">Must-Have Skills</label>
              <div className="flex flex-wrap gap-2">
                {preferences.must_have_skills.map((skill, idx) => (
                  <span
                    key={idx}
                    className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm flex items-center gap-1"
                  >
                    {skill}
                    <button
                      onClick={() => {
                        setPreferences(p => ({
                          ...p,
                          must_have_skills: p.must_have_skills.filter((_, i) => i !== idx)
                        }));
                      }}
                      className="hover:text-purple-900"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            </div>

            {/* Location Type */}
            <div>
              <label className="block text-sm font-medium mb-2">Work Location</label>
              <select
                value={preferences.location_type}
                onChange={(e) => setPreferences({ ...preferences, location_type: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
              >
                <option value="onsite">Onsite</option>
                <option value="hybrid">Hybrid</option>
                <option value="remote">Remote</option>
                <option value="any">Any</option>
              </select>
            </div>

            {/* Experience Range */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Min Experience (years)</label>
                <input
                  type="number"
                  min="0"
                  value={preferences.experience_min || ''}
                  onChange={(e) => setPreferences({ ...preferences, experience_min: parseInt(e.target.value) || null })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                  placeholder="e.g., 3"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Max Experience (years)</label>
                <input
                  type="number"
                  min="0"
                  value={preferences.experience_max || ''}
                  onChange={(e) => setPreferences({ ...preferences, experience_max: parseInt(e.target.value) || null })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                  placeholder="e.g., 7"
                />
              </div>
            </div>

            {/* Data Sources */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Search In {loadingPortals && <span className="text-xs text-gray-500">(Loading...)</span>}
              </label>
              {loadingPortals ? (
                <div className="space-y-2">
                  <div className="animate-pulse h-6 bg-gray-200 rounded"></div>
                  <div className="animate-pulse h-6 bg-gray-200 rounded"></div>
                  <div className="animate-pulse h-6 bg-gray-200 rounded"></div>
                </div>
              ) : (
                <div className="space-y-3">
                  {availablePortals.map((portal) => (
                    <label 
                      key={portal.id} 
                      className="flex items-start gap-3 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={preferences.selected_portal_ids.includes(portal.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setPreferences(p => ({ 
                              ...p, 
                              selected_portal_ids: [...p.selected_portal_ids, portal.id] 
                            }));
                          } else {
                            setPreferences(p => ({ 
                              ...p, 
                              selected_portal_ids: p.selected_portal_ids.filter(id => id !== portal.id) 
                            }));
                          }
                        }}
                        className="w-4 h-4 text-purple-600 rounded mt-1"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900">{portal.name}</span>
                          {portal.is_premium && (
                            <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full">
                              Premium
                            </span>
                          )}
                          {portal.is_default && (
                            <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full">
                              Default
                            </span>
                          )}
                        </div>
                        {portal.description && (
                          <p className="text-xs text-gray-600 mt-1">{portal.description}</p>
                        )}
                        {portal.search_limit_per_day && (
                          <p className="text-xs text-gray-500 mt-1">
                            Limit: {portal.search_limit_per_day} searches/day
                          </p>
                        )}
                      </div>
                    </label>
                  ))}
                  
                  {availablePortals.length === 0 && (
                    <div className="text-center py-6 text-gray-500">
                      <p>No sourcing portals configured.</p>
                      <button 
                        onClick={seedDefaultPortals}
                        className="text-purple-600 hover:text-purple-700 text-sm mt-2"
                      >
                        Set up default portals
                      </button>
                    </div>
                  )}
                </div>
              )}
              
              {/* Max Candidates Per Portal */}
              <div className="mt-4">
                <label className="block text-sm font-medium mb-2">
                  Max Candidates Per Portal
                </label>
                <input
                  type="number"
                  min="5"
                  max="50"
                  value={preferences.max_candidates_per_portal}
                  onChange={(e) => setPreferences({ 
                    ...preferences, 
                    max_candidates_per_portal: parseInt(e.target.value) || 20 
                  })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                  placeholder="20"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Limit candidates returned from each selected portal (5-50)
                </p>
              </div>
            </div>
          </div>

          {/* Action Button */}
          <button
            onClick={startSourcing}
            disabled={loading || preferences.selected_portal_ids.length === 0}
            className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white py-3 rounded-lg font-semibold hover:from-purple-700 hover:to-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
            style={{ zIndex: 1000 }} // Ensure button is above any overlays
          >
            {loading ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Running AI Sourcing...
              </>
            ) : (
              <>
                <Search className="w-5 h-5" />
                Start AI Sourcing
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AISourcingPanel;
